import React, { useState, useEffect } from 'react';
import ExtractionValidator from './ExtractionValidator';
import { extractAndParsePDF } from '../services/simple-pdf-reader';

function ProjectUpload({ onGenerateEstimate }) {
  const [uploadStage, setUploadStage] = useState('upload'); // 'upload' | 'validating' | 'form'
  const [uploading, setUploading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [validatedItems, setValidatedItems] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [projectForm, setProjectForm] = useState({
    name: '',
    location: 'Florida',
    address: '',
    overhead: 0.40,
    profit: 0.15,
    projectType: 'office',
    region: 'southeast',
    // Company branding
    companyName: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyLogo: null,
    // Proposal settings
    showOverheadProfit: false,
    includeContingency: true,
    contingencyPercent: 0.03
  });

  // ADDED: Load from localStorage on mount
  useEffect(() => {
    const savedForm = localStorage.getItem('projectForm');
    const savedStage = localStorage.getItem('uploadStage');
    const savedValidated = localStorage.getItem('validatedItems');

    if (savedForm) {
      try {
        setProjectForm(JSON.parse(savedForm));
        console.log('📂 Loaded project form from localStorage');
      } catch (error) {
        console.error('Failed to load project form:', error);
      }
    }

    if (savedValidated) {
      try {
        const data = JSON.parse(savedValidated);
        setValidatedItems(data);
        if (savedStage === 'form') {
          setUploadStage('form');
          console.log('📂 Restored to form stage');
        }
      } catch (error) {
        console.error('Failed to load validated items:', error);
      }
    }
  }, []);

  // ADDED: Save project form to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('projectForm', JSON.stringify(projectForm));
  }, [projectForm]);

  // ADDED: Save upload stage and validated items
  useEffect(() => {
    localStorage.setItem('uploadStage', uploadStage);
    if (validatedItems) {
      localStorage.setItem('validatedItems', JSON.stringify(validatedItems));
    }
  }, [uploadStage, validatedItems]);

  // Select files and ADD to list (not replace)
  const handleFileSelect = (event) => {
    const files = event.target.files;
    if (!files) return;
    const newFiles = Array.from(files);
    setSelectedFiles(prev => [...prev, ...newFiles]);
    console.log(`📁 Added ${newFiles.length} file(s). Total: ${selectedFiles.length + newFiles.length}`);
    event.target.value = ''; // Reset input for next selection
  };

  // Analyze all selected files
  const handleAnalyzeFiles = async () => {
    if (selectedFiles.length === 0) {
      alert('Please select files first');
      return;
    }
    setUploading(true);
    try {
      console.log(`📤 Analyzing ${selectedFiles.length} file(s):`, selectedFiles.map(f => f.name).join(', '));
      const result = await extractAndParsePDF(selectedFiles);

      console.log('✅ Extraction result:', result);

      if (result?.success) {
        setExtractedData(result);
        setUploadStage('validating');
        setSelectedFiles([]);
      } else {
        alert('Failed: ' + (result?.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error: ' + error.message);
    }
    setUploading(false);
  };

  // Handle validation complete
  const handleValidationComplete = (validatedData) => {
    console.log('✅ Validation complete with items:', validatedData?.items?.length || 0);
    setValidatedItems(validatedData);
    setExtractedData(null);
    setUploadStage('form');
  };

  // Handle form submission
  const handleFormSubmit = (e) => {
    e.preventDefault();

    if (!projectForm.name) {
      alert('Please enter a project name');
      return;
    }

    console.log('📊 Generating estimate...');

    // Extract unique trades from validated items
    const items = validatedItems?.items || [];
    const trades = items.length > 0
      ? [...new Set(items.map(item => item.trade))]
      : [];

    console.log(`📊 Generating estimate with ${items.length} items, ${trades.length} trades`);

    // IMPROVED: Clear localStorage on successful submission
    localStorage.removeItem('projectForm');
    localStorage.removeItem('uploadStage');
    localStorage.removeItem('validatedItems');
    localStorage.removeItem('extractorState');

    // Call parent component to generate estimate
    onGenerateEstimate({
      project: projectForm,
      validatedItems: items,
      trades,
      overhead: projectForm.overhead,
      profit: projectForm.profit,
      projectType: projectForm.projectType,
      region: projectForm.region,
      showOverheadProfit: projectForm.showOverheadProfit,
      includeContingency: projectForm.includeContingency,
      contingencyPercent: projectForm.contingencyPercent,
      companyName: projectForm.companyName,
      companyAddress: projectForm.companyAddress,
      companyPhone: projectForm.companyPhone,
      companyEmail: projectForm.companyEmail,
      companyLogo: projectForm.companyLogo,
      documentType: 'Extracted'
    });
  };

  // Go back to upload
  const handleBack = () => {
    setUploadStage('upload');
    setExtractedData(null);
    // Keep validatedItems for recovery
  };

  // Rendering based on stage
  if (uploadStage === 'validating' && extractedData) {
    return (
      <ExtractionValidator
        extractedData={extractedData}
        onConfirm={handleValidationComplete}
        onBack={handleBack}
      />
    );
  }

  return (
    <div className="project-upload">
      <h2>Create New Project</h2>

      {uploadStage === 'upload' && (
        <div className="upload-section">
          <h3>Step 1: Upload Construction Documents</h3>
          <div className="file-upload">
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              disabled={uploading}
              multiple
              id="pdf-upload"
            />
            <label htmlFor="pdf-upload" className="upload-label">
              {uploading ? (
                <>
                  <div className="spinner"></div>
                  <p>Analyzing {selectedFiles.length} PDF(s)...</p>
                </>
              ) : selectedFiles.length > 0 ? (
                <>
                  <p className="upload-icon">✅</p>
                  <p className="upload-text">{selectedFiles.length} file(s) selected</p>
                  <p className="upload-hint">Click Analyze below to process</p>
                </>
              ) : (
                <>
                  <p className="upload-icon">📄</p>
                  <p className="upload-text">Select PDFs (Architectural, Mechanical, Plumbing)</p>
                  <p className="upload-hint">Select multiple files, then click Analyze</p>
                </>
              )}
            </label>
            {selectedFiles.length > 0 && (
              <div style={{marginTop: '20px', padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '8px'}}>
                <h4>Selected Files ({selectedFiles.length}):</h4>
                <ul style={{listStyle: 'none', padding: 0}}>
                  {selectedFiles.map((f, i) => (
                    <li key={i} style={{padding: '5px', fontSize: '14px', display: 'flex', justifyContent: 'space-between'}}>
                      <span>✓ {f.name}</span>
                      <button
                        type="button"
                        onClick={() => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))}
                        style={{background: 'none', border: 'none', color: 'red', cursor: 'pointer', fontSize: '14px'}}
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => document.getElementById('pdf-upload').click()}
                  disabled={uploading}
                  style={{
                    marginTop: '10px',
                    marginRight: '10px',
                    padding: '8px 16px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  + Add More Files
                </button>
                <button
                  type="button"
                  onClick={handleAnalyzeFiles}
                  disabled={uploading}
                  style={{
                    marginTop: '10px',
                    padding: '10px 20px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  {uploading ? `Analyzing ${selectedFiles.length} file(s)...` : `🚀 Analyze All PDFs (${selectedFiles.length})`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {uploadStage === 'form' && (
        <div className="form-section">
          <h3>Step 2: Project & Company Details</h3>
          <form onSubmit={handleFormSubmit}>

            {/* COMPANY BRANDING */}
            <div className="form-section-title">Your Company</div>
            <div className="form-row">
              <div className="form-group">
                <label>Company Name *</label>
                <input
                  type="text"
                  value={projectForm.companyName}
                  onChange={(e) => setProjectForm({ ...projectForm, companyName: e.target.value })}
                  placeholder="e.g., CRUE Construction"
                  required
                />
              </div>
              <div className="form-group">
                <label>Company Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (ev) => setProjectForm({ ...projectForm, companyLogo: ev.target.result });
                      reader.readAsDataURL(file);
                    }
                  }}
                  style={{ fontSize: '13px' }}
                />
                {projectForm.companyLogo && (
                  <div style={{ marginTop: '8px' }}>
                    <img src={projectForm.companyLogo} alt="Logo preview" style={{ maxHeight: '50px', maxWidth: '150px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px', padding: '4px' }} />
                    <button type="button" onClick={() => setProjectForm({ ...projectForm, companyLogo: null })} style={{ marginLeft: '8px', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '12px' }}>Remove</button>
                  </div>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Company Address</label>
                <input
                  type="text"
                  value={projectForm.companyAddress}
                  onChange={(e) => setProjectForm({ ...projectForm, companyAddress: e.target.value })}
                  placeholder="123 Main St, City, FL 33000"
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={projectForm.companyPhone}
                  onChange={(e) => setProjectForm({ ...projectForm, companyPhone: e.target.value })}
                  placeholder="(727) 555-0100"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Company Email</label>
              <input
                type="email"
                value={projectForm.companyEmail}
                onChange={(e) => setProjectForm({ ...projectForm, companyEmail: e.target.value })}
                placeholder="info@yourcompany.com"
              />
            </div>

            {/* PROJECT INFO */}
            <div className="form-section-title" style={{ marginTop: '24px' }}>Project Details</div>
            <div className="form-group">
              <label>Project / Client Name *</label>
              <input
                type="text"
                value={projectForm.name}
                onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                placeholder="e.g., American Heart Association"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={projectForm.location}
                  onChange={(e) => setProjectForm({ ...projectForm, location: e.target.value })}
                  placeholder="City, State"
                />
              </div>
              <div className="form-group">
                <label>Client Address</label>
                <input
                  type="text"
                  value={projectForm.address}
                  onChange={(e) => setProjectForm({ ...projectForm, address: e.target.value })}
                  placeholder="Street address"
                />
              </div>
            </div>

            {/* PROJECT TYPE & REGION */}
            <div className="form-row" style={{ marginTop: '16px' }}>
              <div className="form-group">
                <label>Project Type</label>
                <select
                  value={projectForm.projectType}
                  onChange={(e) => setProjectForm({ ...projectForm, projectType: e.target.value })}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', width: '100%', fontSize: '14px' }}
                >
                  <option value="office">Commercial Office</option>
                  <option value="medical">Medical / Healthcare</option>
                  <option value="retail">Retail / Restaurant</option>
                  <option value="industrial">Industrial / Warehouse</option>
                  <option value="school">School / University</option>
                  <option value="multifamily">Multifamily / Residential</option>
                  <option value="renovation">General Renovation</option>
                </select>
                <span style={{ fontSize: '11px', color: '#888' }}>Affects cost multipliers</span>
              </div>
              <div className="form-group">
                <label>Region</label>
                <select
                  value={projectForm.region}
                  onChange={(e) => setProjectForm({ ...projectForm, region: e.target.value })}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ddd', width: '100%', fontSize: '14px' }}
                >
                  <option value="southeast">Southeast (FL, GA, SC) — 0.88x</option>
                  <option value="south">South (TX, LA, AR) — 0.90x</option>
                  <option value="midwest">Midwest (OH, IN, MI) — 0.96x</option>
                  <option value="northeast">Northeast (NY, NJ, MA) — 1.25x</option>
                  <option value="west">West Coast (CA, WA, OR) — 1.22x</option>
                  <option value="southwest">Southwest (AZ, NV, CO) — 1.05x</option>
                  <option value="national">National Average — 1.00x</option>
                </select>
                <span style={{ fontSize: '11px', color: '#888' }}>Regional cost adjustment</span>
              </div>
            </div>

            {/* OVERHEAD & PROFIT */}
            <div className="form-section-title" style={{ marginTop: '24px' }}>Pricing Settings</div>

            <div className="form-row">
              <div className="form-group">
                <label>Overhead: {(projectForm.overhead * 100).toFixed(0)}%</label>
                <input
                  type="range" min="0.10" max="0.60" step="0.05"
                  value={projectForm.overhead}
                  onChange={(e) => setProjectForm({ ...projectForm, overhead: parseFloat(e.target.value) })}
                />
                <span style={{ fontSize: '11px', color: '#888' }}>Typical: 25-40%</span>
              </div>
              <div className="form-group">
                <label>Profit: {(projectForm.profit * 100).toFixed(0)}%</label>
                <input
                  type="range" min="0.05" max="0.30" step="0.05"
                  value={projectForm.profit}
                  onChange={(e) => setProjectForm({ ...projectForm, profit: parseFloat(e.target.value) })}
                />
                <span style={{ fontSize: '11px', color: '#888' }}>Typical: 10-15%</span>
              </div>
            </div>

            {/* OVERHEAD DISPLAY TOGGLE */}
            <div className="form-group" style={{ background: '#f9f9f9', padding: '16px', borderRadius: '8px', border: '1px solid #eee' }}>
              <label style={{ fontWeight: 'bold', marginBottom: '12px', display: 'block' }}>Proposal Style</label>
              <div style={{ display: 'flex', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '10px 16px', borderRadius: '6px', border: `2px solid ${!projectForm.showOverheadProfit ? '#dc2626' : '#ddd'}`, background: !projectForm.showOverheadProfit ? '#fff5f5' : 'white' }}>
                  <input
                    type="radio" name="proposalStyle"
                    checked={!projectForm.showOverheadProfit}
                    onChange={() => setProjectForm({ ...projectForm, showOverheadProfit: false })}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '13px' }}>Baked In</div>
                    <div style={{ fontSize: '11px', color: '#888' }}>Overhead & profit included in division costs</div>
                  </div>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '10px 16px', borderRadius: '6px', border: `2px solid ${projectForm.showOverheadProfit ? '#dc2626' : '#ddd'}`, background: projectForm.showOverheadProfit ? '#fff5f5' : 'white' }}>
                  <input
                    type="radio" name="proposalStyle"
                    checked={projectForm.showOverheadProfit}
                    onChange={() => setProjectForm({ ...projectForm, showOverheadProfit: true })}
                  />
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '13px' }}>Itemized</div>
                    <div style={{ fontSize: '11px', color: '#888' }}>Overhead & profit shown as separate lines</div>
                  </div>
                </label>
              </div>
            </div>

            {/* CONTINGENCY TOGGLE */}
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
              <input
                type="checkbox"
                id="contingency"
                checked={projectForm.includeContingency}
                onChange={(e) => setProjectForm({ ...projectForm, includeContingency: e.target.checked })}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <label htmlFor="contingency" style={{ cursor: 'pointer', fontWeight: 'normal' }}>
                Include Owner Contingency recommendation ({(projectForm.contingencyPercent * 100).toFixed(0)}%)
              </label>
            </div>

            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={handleBack}>Back</button>
              <button type="submit" className="btn btn-primary" disabled={!validatedItems || !validatedItems.items || validatedItems.items.length === 0}>
                Generate Estimate ({validatedItems?.items?.length || 0} items)
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export default ProjectUpload;
